import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowUturnLeftIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  CheckIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { Tooltip } from "@fronterahealth/frontera-ui-components";

interface ApprovalActionBarProps {
  isApproved: boolean;
  isDisabled?: boolean;
  isPending?: boolean;
  /**
   * Visually dims the Approve button without setting the native `disabled`
   * attribute, so `onApprove` still fires. Used to let callers observe (and
   * give feedback on) clicks that land while approval is blocked for a
   * reason other than an in-flight mutation — e.g. the chart isn't ready yet.
   */
  isApproveWaiting?: boolean;
  onApprove: () => void;
  onRevoke: () => void;
  onPreviousGoal?: () => void;
  onNextGoal?: () => void;
  onPreviousTarget?: () => void;
  onNextTarget?: () => void;
  onMenuClick?: () => void;
  /**
   * Whether this report supports targets. When false, hides the target
   * navigation buttons (and their Shift+↑/↓ shortcuts) so reports without
   * target capabilities don't show actions that go nowhere.
   */
  showTargetNavigation?: boolean;
}

const IconButton: React.FC<{
  icon: React.ElementType;
  onClick?: () => void;
  disabled?: boolean;
  tooltip?: string;
  shortcut?: string;
}> = ({ icon: Icon, onClick, disabled, tooltip, shortcut }) => {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={twMerge(
        "flex h-10 w-10 items-center justify-center rounded-md transition-colors",
        disabled ? "cursor-not-allowed text-limestone-300" : "cursor-pointer text-text-primary hover:bg-limestone-100",
      )}
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
    </button>
  );

  if (tooltip) {
    const content = shortcut ? (
      <span className="flex items-center gap-1.5">
        <span>{tooltip}</span>
        <span className="flex items-center gap-1">
          {shortcut.split(" + ").map((keyName, i, arr) => (
            <React.Fragment key={keyName}>
              <kbd className="rounded border border-white/25 bg-white/10 px-1.5 py-0.5 text-xs font-sans font-medium leading-none">
                {keyName}
              </kbd>
              {i < arr.length - 1 ? <span className="text-xs text-white/60">+</span> : null}
            </React.Fragment>
          ))}
        </span>
      </span>
    ) : (
      tooltip
    );

    return (
      <Tooltip text={content} showInfoIcon={false} placement="top">
        {button}
      </Tooltip>
    );
  }

  return button;
};

export const ApprovalActionBar: React.FC<ApprovalActionBarProps> = ({
  isApproved,
  isDisabled = false,
  isPending = false,
  isApproveWaiting = false,
  onApprove,
  onRevoke,
  onPreviousGoal,
  onNextGoal,
  onPreviousTarget,
  onNextTarget,
  onMenuClick,
  showTargetNavigation = true,
}) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      if (tagName === "input" || tagName === "textarea" || target.isContentEditable) return;

      if (!e.shiftKey) return;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          onPreviousGoal?.();
          break;
        case "ArrowRight":
          e.preventDefault();
          onNextGoal?.();
          break;
        case "ArrowUp":
          if (!showTargetNavigation) break;
          e.preventDefault();
          onPreviousTarget?.();
          break;
        case "ArrowDown":
          if (!showTargetNavigation) break;
          e.preventDefault();
          onNextTarget?.();
          break;
      }
    },
    [onPreviousGoal, onNextGoal, onPreviousTarget, onNextTarget, showTargetNavigation],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Push bottom-anchored toasts above this bar while it's mounted. The CSS
  // variable is consumed in notifications.css. ~80px ≈ bar height (~64px) +
  // bottom margin (16px), so toasts land just above the bar with no overlap.
  useEffect(() => {
    document.body.style.setProperty("--actions-bar-offset", "80px");
    return () => {
      document.body.style.removeProperty("--actions-bar-offset");
    };
  }, []);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center justify-end p-4">
      <div
        className={twMerge(
          "flex items-center gap-4 rounded-2xl border border-limestone-500 bg-white px-6 py-3 shadow-lg",
          isDisabled && "opacity-25",
        )}
      >
        {/* Navigation Buttons — kept in a tight cluster so they read as one group */}
        <div className="flex items-center gap-1">
          <IconButton
            icon={ArrowLeftIcon}
            onClick={onPreviousGoal}
            disabled={isDisabled || !onPreviousGoal}
            tooltip="Previous goal"
            shortcut="Shift + ←"
          />
          <IconButton
            icon={ArrowRightIcon}
            onClick={onNextGoal}
            disabled={isDisabled || !onNextGoal}
            tooltip="Next goal"
            shortcut="Shift + →"
          />
          {showTargetNavigation && (
            <>
              <IconButton
                icon={BarsArrowDownIcon}
                onClick={onNextTarget}
                disabled={isDisabled || !onNextTarget}
                tooltip="Next target"
                shortcut="Shift + ↓"
              />
              <IconButton
                icon={BarsArrowUpIcon}
                onClick={onPreviousTarget}
                disabled={isDisabled || !onPreviousTarget}
                tooltip="Previous target"
                shortcut="Shift + ↑"
              />
            </>
          )}
        </div>

        {/* Approve / Revoke Button */}
        <button
          type="button"
          onClick={isApproved ? onRevoke : onApprove}
          disabled={isDisabled || isPending}
          className={twMerge(
            "flex h-8 items-center justify-center gap-2 rounded-full border border-green-700 bg-white px-4 text-sm font-semibold text-green-700 transition-colors",
            isDisabled || isPending || isApproveWaiting
              ? "cursor-not-allowed opacity-25"
              : "cursor-pointer hover:bg-green-50 active:bg-green-100",
          )}
        >
          {(() => {
            if (isPending) return <ArrowPathIcon className="h-4 w-4 animate-spin" strokeWidth={2} />;
            if (isApproved) return <ArrowUturnLeftIcon className="h-4 w-4" strokeWidth={2} />;
            return <CheckIcon className="h-4 w-4" strokeWidth={2} />;
          })()}
          {isApproved ? "Revoke" : "Approve"}
        </button>

        {/* Divider + Menu */}
        {onMenuClick && (
          <>
            <div className="h-8 w-px bg-limestone-200" />
            <IconButton icon={EllipsisVerticalIcon} onClick={onMenuClick} disabled={isDisabled} />
          </>
        )}
      </div>
    </div>
  );
};
